/* eslint-disable sonarjs/cognitive-complexity */
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Action } from '@ngrx/store';
import {
  UserActionTypes,
  GetUserProfileSuccess,
  GetUserProfileError,
  GetLoginUserProfileSuccess,
  GetLoginUserProfileError,
  UpdateUserProfile,
  UpdateUserProfileError,
  LoadTinsSuccess,
  LoadTinsError,
  GetSecurityQuestionsSuccess,
  GetSecurityQuestionsError,
  UpdateUserProfileSuccess,
  LoadTinsCached,
  UpdateSkipCount,
  UpdateSkipCountSuccess,
  UpdateSkipCountError,
  LoadTinFunctionsSuccess,
  LoadTinFunctionsError,
  UpdateUserProfileIndicator,
  UpdateWatchAudit,
  UpdateWatchAuditSuccess,
  UpdateWatchAuditError,
  UpdateLoginAudit,
  UpdateLoginAuditSuccess,
  UpdateLoginAuditError,
  GetAuthMethodsStatus,
  GetAuthMethodsStatusSuccess,
  GetAuthMethodsStatusError,
} from './user.actions';

import { of, Observable } from 'rxjs';
import {
  map,
  switchMap,
  catchError,
  concatMap,
  withLatestFrom,
  tap,
} from 'rxjs/operators';

import {
  AuthMethod,
  LoginProfileResponse,
  LoginProfileResponseEks,
  UserProfile,
} from '../user-profile/user-profile.model';
import { UserProfileService } from '../user-profile/user-profile.service';
import { UserTinsService } from '../user-tins/tins.service';
import { UserSecurityQuestionsService } from '../user-security-questions/security-questions.service';
import { UserFacade } from './user.facade';
import { ShellTrackHandlers } from '@cigna/chcp/shared/analytics-util';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { AppContext } from '@cigna/chcp/shared/util';
import {
  APP_CONTEXT,
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
} from '@cigna/chcp/shared/environment-util';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FeatureTogglesFacade } from '@cigna/shared/angular/features-feature';

@Injectable()
export class UserEffects {
  isAuth0Login: boolean;
  constructor(
    private actions$: Actions,
    private userFacade: UserFacade,
    private userProfileService: UserProfileService,
    private tinService: UserTinsService,
    private securityQuestionService: UserSecurityQuestionsService,
    private analytics: ShellTrackHandlers,
    private authFacade: AuthFacade,
    private router: Router,
    private featureTogglesFacade: FeatureTogglesFacade,
    @Inject(APP_CONTEXT) public appContext: AppContext,
    @Inject(ENVIRONMENT_CONFIG) private envConfig: EnvironmentConfig,
  ) {
    this.isAuth0Login = this.envConfig.authMethod === 'auth0';
  }

  getAuthMethodsStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetAuthMethodsStatus),
      switchMap(() =>
        this.userProfileService.getAuthMethodsStatus().pipe(
          map((data: AuthMethod[]) => new GetAuthMethodsStatusSuccess(data)),
          catchError(() => of(new GetAuthMethodsStatusError())),
        ),
      ),
    ),
  );

  getUserProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetUserProfile),
      switchMap(() =>
        this.userProfileService.getUserProfile().pipe(
          map((data: any) => new GetUserProfileSuccess(data)),
          catchError((err) => of(new GetUserProfileError(err))),
        ),
      ),
    ),
  );

  getUserProfileServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.GetUserProfileError),
        map((action: GetUserProfileError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/profile/v2/details:GET',
            error,
            'Error loading user profile.',
          );
          this.router.navigateByUrl('/unavailable', {
            skipLocationChange: true,
          });
        }),
      ),
    { dispatch: false },
  );

  getLoginUserProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetLoginUserProfile),
      withLatestFrom(this.authFacade.isDentalUser$),
      switchMap(([, isDentalUser]) =>
        this.userProfileService
          .getLoginUserProfile(isDentalUser as boolean)
          .pipe(
            map((data: LoginProfileResponse | LoginProfileResponseEks) =>
              this.isAuth0Login
                ? new GetLoginUserProfileSuccess(data as LoginProfileResponse)
                : new GetLoginUserProfileSuccess(
                    data as LoginProfileResponseEks,
                  ),
            ),

            catchError((err) => of(new GetLoginUserProfileError(err))),
          ),
      ),
    ),
  );

  getLoginUserProfileServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.GetLoginUserProfileError),
        map((action: GetLoginUserProfileError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            `/identity/${
              this.appContext === 'evernorth' ? 'epp' : 'med'
            }/v1/login-user-profile:GET`,
            error,
            'Error loading login user profile.',
          );
          this.router.navigateByUrl('/unavailable', {
            skipLocationChange: true,
          });
        }),
      ),
    { dispatch: false },
  );

  updateUserProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateUserProfile),
      map((action: UpdateUserProfile) => action.payload),
      concatMap((userData) =>
        this.userProfileService.updateUserProfile(userData).pipe(
          map((data: UserProfile) => new UpdateUserProfileSuccess(data)),
          catchError((err) => of(new UpdateUserProfileError(err))),
        ),
      ),
    ),
  );

  updateUserAgreementIndicator$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateUserProfileIndicator),
      map((action: UpdateUserProfileIndicator) => action.payload),
      concatMap((userData) =>
        this.userProfileService.updateUserProfileIndicators(userData).pipe(
          map(
            (data: Partial<UserProfile>) => new UpdateUserProfileSuccess(data),
          ),
          catchError((err) => of(new UpdateUserProfileError(err))),
        ),
      ),
    ),
  );

  updateUserProfileServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.UpdateUserProfileError),
        map((action: UpdateUserProfileError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/preferences/v2/details:PUT',
            error,
            'Error updating user profile.',
          );
        }),
      ),
    { dispatch: false },
  );

  loadTins$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.LoadTins),
      withLatestFrom(this.userFacade.unFilteredTins$),
      switchMap(([, tins]) => {
        if (tins.length > 0) {
          return of(new LoadTinsCached());
        }
        return this.tinService.getTins().pipe(
          withLatestFrom(this.authFacade.lob$),
          map(
            ([result, lob]) =>
              new LoadTinsSuccess({ tins: result, lob: lob as string }),
          ),
          catchError((err) => of(new LoadTinsError(err))),
        );
      }),
    ),
  );

  loadTinFunctions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.LoadTinFunctions),
      switchMap(
        ({
          payload,
        }: {
          payload: { entitlements: string[]; functions: string[] };
        }) =>
          this.tinService
            .getTinFunctions(payload.entitlements, payload.functions)
            .pipe(
              map((result) => new LoadTinFunctionsSuccess({ tins: result })),
              catchError((err) => of(new LoadTinFunctionsError(err))),
            ),
      ),
    ),
  );

  loadTinsServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.LoadTinsError),
        map((action: LoadTinsError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/profile/v2/tinDetails:GET',
            error,
            'Error loading user tins.',
          );
        }),
      ),
    { dispatch: false },
  );

  updateWatchAudit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateWatchAudit),
      switchMap(() =>
        this.userProfileService.updateWatchAudit().pipe(
          map(() => new UpdateWatchAuditSuccess()),
          catchError((err) => of(new UpdateWatchAuditError(err))),
        ),
      ),
    ),
  );

  updateLoginAudit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateLoginAudit),
      switchMap(() =>
        this.userProfileService.updateLoginAudit().pipe(
          map(() => new UpdateLoginAuditSuccess()),
          catchError((err) => of(new UpdateLoginAuditError(err))),
        ),
      ),
    ),
  );

  getSecurityQuestions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetSecurityQuestions),
      switchMap(() =>
        this.securityQuestionService.getSecurityQuestions().pipe(
          map((result) => new GetSecurityQuestionsSuccess(result)),
          catchError((err) => of(new GetSecurityQuestionsError(err))),
        ),
      ),
    ),
  );

  updateSkipCount$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateSkipCount),
      map((action: UpdateSkipCount) => action.payload),
      concatMap((userData) =>
        this.userProfileService
          .updateUserProfile(
            userData.type === 'popup'
              ? { contactCntr: userData.count }
              : { emailVerifySkipCount: userData.count },
          )
          .pipe(
            map((data: UserProfile) => new UpdateSkipCountSuccess(data)),
            catchError((_err) => of(new UpdateSkipCountError())),
          ),
      ),
    ),
  );
}
